/** Reusable table component for displaying accounts with common columns and actions. */
import React, { useMemo } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Paper,
  Checkbox,
  Menu,
  MenuItem,
  Button,
  Typography,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  VisibilityOff as VisibilityOffIcon,
} from "@mui/icons-material";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  createColumnHelper,
  SortingState,
  RowSelectionState,
  ColumnDef,
  flexRender,
} from "@tanstack/react-table";

/** Account interface matching backend schema. */
export interface Account {
  id: string;
  tenant: string;
  account: string;
  account_type: string;
  customer_type?: string;
  balance: number;
  credit_limit: number | null;
  max_concurrent_channels?: number | null;
  disabled: boolean;
  created_at?: string;
  updated_at?: string;
}

/** Column helper for TanStack Table. */
const columnHelper = createColumnHelper<Account>();

interface AccountTableProps {
  accounts: Account[];
  columns: ColumnDef<Account>[];
  onEdit: (account: Account) => void;
  onDelete: (accountId: string) => void;
  typeSpecificActions?: (account: Account) => React.ReactNode;
  onViewCDRs?: (account: Account) => void;
  onViewPIN?: (account: Account) => void;
  selectable?: boolean;
  onSelectionChange?: (selectedRows: RowSelectionState) => void;
}

/** Account type chip component. */
function AccountTypeChip({ accountType }: { accountType: string }) {
  const isPrepaid = accountType === "*prepaid";
  return (
    <Box
      sx={{
        px: 1,
        py: 0.5,
        borderRadius: 1,
        bgcolor: isPrepaid ? "primary.light" : "secondary.light",
        color: isPrepaid ? "primary.contrastText" : "secondary.contrastText",
        fontSize: "0.75rem",
        fontWeight: 600,
      }}
    >
      {isPrepaid ? "Prepaid" : "Postpaid"}
    </Box>
  );
}

/** Status chip component. */
function StatusChip({ disabled }: { disabled: boolean }) {
  return (
    <Box
      sx={{
        px: 1,
        py: 0.5,
        borderRadius: 1,
        bgcolor: disabled ? "error.light" : "success.light",
        color: disabled ? "error.contrastText" : "success.contrastText",
        fontSize: "0.75rem",
        fontWeight: 600,
      }}
    >
      {disabled ? "Disabled" : "Active"}
    </Box>
  );
}

/** Reusable account table component. */
export default function AccountTable({
  accounts,
  columns,
  onEdit,
  onDelete,
  typeSpecificActions,
  onViewCDRs,
  selectable = true,
  onSelectionChange,
}: AccountTableProps) {
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [rowSelection, setRowSelection] = React.useState<RowSelectionState>({});
  const [columnVisibilityMenuAnchor, setColumnVisibilityMenuAnchor] =
    React.useState<null | HTMLElement>(null);
  const [hiddenColumns, setHiddenColumns] = React.useState<Set<string>>(new Set());

  /** Toggle column visibility. */
  const toggleColumnVisibility = (columnId: string) => {
    const newHidden = new Set(hiddenColumns);
    if (newHidden.has(columnId)) {
      newHidden.delete(columnId);
    } else {
      newHidden.add(columnId);
    }
    setHiddenColumns(newHidden);
    setColumnVisibilityMenuAnchor(null);
  };

  /** Build columns with common columns + type-specific columns. */
  const tableColumns = useMemo(() => {
    const baseColumns: ColumnDef<Account, any>[] = [];

    // Checkbox column for row selection
    if (selectable) {
      baseColumns.push(
        columnHelper.display({
          id: "select",
          header: ({ table }) => (
            <Checkbox
              checked={table.getIsAllRowsSelected()}
              indeterminate={table.getIsSomeRowsSelected()}
              onChange={table.getToggleAllRowsSelectedHandler()}
            />
          ),
          cell: ({ row }) => (
            <Checkbox
              checked={row.getIsSelected()}
              indeterminate={row.getIsSomeSelected()}
              onChange={row.getToggleSelectedHandler()}
            />
          ),
          enableSorting: false,
          enableHiding: false,
        })
      );
    }

    // Add type-specific columns
    baseColumns.push(...columns);

    // Common actions column
    baseColumns.push(
      columnHelper.accessor((row) => row.id, {
        id: "actions",
        header: "Actions",
        cell: (info) => (
          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
            {typeSpecificActions && typeSpecificActions(accounts[info.row.index])}
            {onViewCDRs && (
              <Tooltip title="View CDRs">
                <IconButton
                  size="small"
                  onClick={() => onViewCDRs(accounts[info.row.index])}
                  color="info"
                 aria-label="View">
                  <ViewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Edit Account">
              <IconButton
                size="small"
                onClick={() => onEdit(accounts[info.row.index])}
                color="primary"
               aria-label="Edit">
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete Account">
              <IconButton
                size="small"
                onClick={() => onDelete(accounts[info.row.index].id)}
                color="error"
               aria-label="Delete">
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ),
        enableSorting: false,
      })
    );

    return baseColumns;
  }, [columns, accounts, selectable, typeSpecificActions, onViewCDRs, onEdit, onDelete]);

  /** Table instance. */
  const table = useReactTable({
    data: accounts,
    columns: tableColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting,
      rowSelection,
    },
    onSortingChange: setSorting,
    onRowSelectionChange: (updater) => {
      const newSelection =
        typeof updater === "function" ? updater(rowSelection) : updater;
      setRowSelection(newSelection);
      if (onSelectionChange) {
        onSelectionChange(newSelection);
      }
    },
  });

  /** Handle row selection change. */
  React.useEffect(() => {
    if (onSelectionChange) {
      onSelectionChange(rowSelection);
    }
  }, [rowSelection, onSelectionChange]);

  return (
    <Box>
      {/* Column visibility menu */}
      {selectable && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
          <Button
            size="small"
            onClick={(e) => setColumnVisibilityMenuAnchor(e.currentTarget)}
            startIcon={<VisibilityOffIcon />}
          >
            Column Visibility
          </Button>
          <Menu
            anchorEl={columnVisibilityMenuAnchor}
            open={Boolean(columnVisibilityMenuAnchor)}
            onClose={() => setColumnVisibilityMenuAnchor(null)}
          >
            {table.getAllLeafColumns().map((column) => {
              if (column.id === "select" || column.id === "actions") return null;
              return (
                <MenuItem
                  key={column.id}
                  onClick={() => toggleColumnVisibility(column.id)}
                >
                  <Checkbox
                    size="small"
                    checked={!hiddenColumns.has(column.id)}
                    onChange={() => toggleColumnVisibility(column.id)}
                  />
                  <Typography variant="body2">
                    {typeof column.columnDef.header === "string"
                      ? column.columnDef.header
                      : column.id}
                  </Typography>
                </MenuItem>
              );
            })}
          </Menu>
        </Box>
      )}

      {/* Table */}
      <TableContainer
        component={Paper}
        elevation={2}
        sx={{ overflowX: "auto" }}
      >
        <Table>
          <TableHead>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  if (hiddenColumns.has(header.id)) return null;
                  return (
                    <TableCell
                      key={header.id}
                      sortDirection={
                        header.column.getIsSorted()
                          ? (header.column.getIsSorted() as "asc" | "desc")
                          : false
                      }
                      sx={{
                        fontWeight: "bold",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {header.column.getCanSort() ? (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            cursor: "pointer",
                            userSelect: "none",
                          }}
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                          <Box sx={{ ml: 1 }}>
                            {header.column.getIsSorted() === "asc"
                              ? "▲"
                              : header.column.getIsSorted() === "desc"
                              ? "▼"
                              : ""}
                          </Box>
                        </Box>
                      ) : (
                        flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableHead>
          <TableBody>
            {table.getRowModel().rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={
                    table.getAllLeafColumns().filter(
                      (c) => !hiddenColumns.has(c.id)
                    ).length
                  }
                  sx={{ textAlign: "center", py: 4 }}
                >
                  <Typography variant="body2" color="text.secondary">
                    No accounts found.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  sx={{
                    "&:last-child td, &:last-child th": { border: 0 },
                    "&:hover": { bgcolor: "action.hover" },
                  }}
                >
                  {row.getVisibleCells().map((cell) => {
                    if (hiddenColumns.has(cell.column.id)) return null;
                    return (
                      <TableCell key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

/** Common column definitions for reuse. */
export const CommonColumns = {
  accountName: columnHelper.accessor("account", {
    header: "Account Name",
    cell: (info) => info.getValue(),
    enableSorting: true,
  }),

  balance: columnHelper.accessor("balance", {
    header: "Balance",
    cell: (info) => `$${info.getValue().toFixed(2)}`,
    enableSorting: true,
  }),

  creditLimit: columnHelper.accessor("credit_limit", {
    header: "Credit Limit",
    cell: (info) => (info.getValue() ? `$${info.getValue()?.toFixed(2)}` : "-"),
    enableSorting: true,
  }),

  channelCap: columnHelper.accessor("max_concurrent_channels", {
    header: "Ch. Cap",
    cell: (info) =>
      info.getValue() === null || info.getValue() === undefined
        ? "∞"
        : info.getValue(),
    enableSorting: true,
  }),

  accountType: columnHelper.accessor("account_type", {
    header: "Billing Type",
    cell: (info) => <AccountTypeChip accountType={info.getValue()} />,
    enableSorting: true,
  }),

  status: columnHelper.accessor("disabled", {
    header: "Status",
    cell: (info) => <StatusChip disabled={info.getValue()} />,
    enableSorting: true,
  }),
};
